import {
  Box,
  Button,
  CircularProgress,
  MenuItem,
  Select,
  Typography,
  Modal,
  useTheme,
  FormHelperText,
  useMediaQuery,
  TextField,
} from '@mui/material';
import { useEffect, useState, useRef } from 'react';
import { useForm, Controller, useFieldArray } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useTranslation } from 'react-i18next';
import { useGetTemplates } from '../../hooks/useGetTemplates';
import { ControlledTextField } from '@frontend/features/common/components/ControlledTextField';
import { useSendTemplate } from '../../hooks/useSendTemplate';
import { useSendTemplateWithFile } from '../../hooks/useSendTemplateWithFile';
import { useRenderTemplateParams } from '../../hooks/useRenderTemplateParams';
import { useOptionalWhatsappClient } from '../WhatsappClientContext';
import { LoadingButton } from '@mui/lab';
import { AttachFile } from '@mui/icons-material';

export function replacePlaceholders(template: string, values: string[]) {
  return template.replace(/{{(\d+)}}/g, (_, index) => {
    const i = parseInt(index, 10) - 1;
    return values[i] ?? '';
  });
}

const formSchema = z.object({
  templateId: z.string().nonempty('whatsapp.templateMessenger.selectTemplate'),
  placeholders: z.array(z.object({ value: z.string().nonempty('Campo requerido') })),
  file: z.any().optional(),
});

type FormValues = z.infer<typeof formSchema>;

interface TemplateMessengerTarget {
  id: string;
  agencyId: number;
  whatsappNumber: string | null;
}

export const TemplateMessenger = ({
  open,
  onClose,
  onSent,
  target,
  caseId,
}: {
  open: boolean;
  onClose: () => void;
  onSent?: () => void;
  target?: TemplateMessengerTarget;
  caseId?: number;
}) => {
  const { t } = useTranslation();
  const whatsappClient = useOptionalWhatsappClient();
  const messagingTarget = target ?? whatsappClient?.clientData;
  const templateCaseId = caseId ?? whatsappClient?.currentCaseId;
  const sendTemplate = useSendTemplate(messagingTarget?.agencyId, templateCaseId);
  const sendTemplateWithFile = useSendTemplateWithFile(messagingTarget?.agencyId, templateCaseId);
  const { mutateAsync: renderTemplateParamsAsync } = useRenderTemplateParams(messagingTarget?.agencyId);
  const { data, isLoading } = useGetTemplates(messagingTarget?.agencyId);
  const { palette, breakpoints } = useTheme();
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const {
    control,
    watch,
    handleSubmit,
    reset,
    setValue,
    getValues,
    formState: { errors },
  } = useForm<FormValues>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      templateId: '',
      placeholders: [],
      file: undefined,
    },
    mode: 'onChange',
  });

  const templateId = watch('templateId');
  const placeholdersValues = watch('placeholders');

  const [searchText, setSearchText] = useState('');

  const enabledTemplates = (data?.templates || []).filter((template) => template.enabled !== false);
  const availableTemplates = enabledTemplates.filter((template) => {
    const searchValue = searchText.trim().toLowerCase();
    if (!searchValue) return true;
    const name = template.elementName || '';
    const id = template.id || '';
    return `${name} ${id}`.toLowerCase().includes(searchValue);
  });

  const selectedTemplate = enabledTemplates.find((t) => t.id === templateId);
  const templateOptions =
    selectedTemplate && !availableTemplates.some((t) => t.id === templateId)
      ? [selectedTemplate, ...availableTemplates]
      : availableTemplates;

  // Determinar si la plantilla requiere un archivo (tipo documento o imagen)
  const templateTypeLC = selectedTemplate?.templateType?.toLowerCase() || '';
  const isDocumentTemplate = templateTypeLC === 'document';
  const isImageTemplate = templateTypeLC === 'image';
  const requiresFile = isDocumentTemplate || isImageTemplate;

  const { fields, replace } = useFieldArray({
    control,
    name: 'placeholders',
  });

  useEffect(() => {
    if (!open) {
      reset({
        templateId: '',
        placeholders: [],
        file: undefined,
      });
      setSelectedFile(null);
      setFileError(null);
      setSearchText('');
    }
  }, [open, reset]);

  useEffect(() => {
    if (!selectedTemplate) {
      replace([]);
      return;
    }

    const placeholderMatches = selectedTemplate.data.match(/{{\d+}}/g) || [];
    const emptyPlaceholders = placeholderMatches.map(() => ({ value: '' }));

    replace(emptyPlaceholders);
    setSelectedFile(null);
    setFileError(null);
  }, [replace, selectedTemplate]);

  useEffect(() => {
    let cancelled = false;

    const prefillSelectedTemplatePlaceholders = async () => {
      if (!selectedTemplate || !templateCaseId) {
        return;
      }

      const placeholderMatches = selectedTemplate.data.match(/{{\d+}}/g) || [];
      const hasMappedPlaceholder = selectedTemplate.placeholderMappings?.some((mapping) => mapping.trim() !== '');

      if (!hasMappedPlaceholder || !placeholderMatches.length) {
        return;
      }

      try {
        const response = await renderTemplateParamsAsync({
          templateId: selectedTemplate.id,
          data: { caseId: templateCaseId },
        });

        if (cancelled) {
          return;
        }

        const currentPlaceholderValues = getValues('placeholders') || [];
        const hasUserInput = currentPlaceholderValues.some((item) => item?.value?.trim());

        if (hasUserInput) {
          return;
        }

        replace(
          placeholderMatches.map((_, index) => ({
            value: response.renderedParams[index] ?? '',
          })),
        );
      } catch {
        if (cancelled) {
          return;
        }
      }
    };

    prefillSelectedTemplatePlaceholders();

    return () => {
      cancelled = true;
    };
  }, [templateCaseId, getValues, renderTemplateParamsAsync, replace, selectedTemplate]);

  useEffect(() => {
    if (templateId && !selectedTemplate) {
      setValue('templateId', '');
    }
  }, [templateId, selectedTemplate, setValue]);

  const handleClose = () => {
    reset({
      templateId: '',
      placeholders: [],
      file: undefined,
    });
    setSelectedFile(null);
    setFileError(null);
    setSearchText('');
    onClose();
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const [file] = event.target.files;
      setSelectedFile(file);
      setFileError(null);
    }
  };

  const onSubmit = async (data: FormValues) => {
    if (!selectedTemplate) return;

    const { templateId, placeholders } = data;
    const placeholderValues = placeholders.map((p) => p.value);
    const finalText = replacePlaceholders(selectedTemplate.data, placeholderValues);

    if (!messagingTarget?.whatsappNumber) return;

    // Check if file is required but not provided
    if (requiresFile && !selectedFile) {
      setFileError(t('whatsapp.templateMessenger.fileRequired'));
      return;
    }

    const onSuccess = () => {
      reset({
        templateId: '',
        placeholders: [],
        file: undefined,
      });
      setSelectedFile(null);
      setFileError(null);
      onSent?.();
      onClose();
    };

    if (requiresFile && selectedFile) {
      // Use sendTemplateWithFile for document or image templates
      sendTemplateWithFile.mutate(
        {
          file: selectedFile,
          clientId: messagingTarget.id,
          templateName: selectedTemplate.elementName,
          languageCode: selectedTemplate.languageCode || 'es',
          bodyParameters:
            placeholderValues.length > 0
              ? placeholderValues.map((value) => ({ type: 'text', text: value }))
              : undefined,
        },
        { onSuccess },
      );
    } else {
      // Use regular sendTemplate for text templates
      sendTemplate.mutate(
        {
          destination: messagingTarget.whatsappNumber,
          templateId: templateId,
          templateParams: placeholderValues || [],
          messageText: finalText,
        },
        { onSuccess },
      );
    }
  };

  const isMobile = useMediaQuery(breakpoints.down('md'));

  if (isLoading && !isMobile) return <CircularProgress />;

  const canSubmit = () => {
    if (!templateId) return false;

    if (requiresFile && !selectedFile) return false;

    if (fields.length > 0) {
      return placeholdersValues?.every((p) => p?.value.trim() !== '');
    }

    return true;
  };

  const placeholderPreviewValues = (placeholdersValues || []).map((placeholder) => placeholder?.value || '');
  const previewText = selectedTemplate ? replacePlaceholders(selectedTemplate.data, placeholderPreviewValues) : '';

  return (
    <Modal open={open} onClose={handleClose}>
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          minWidth: 300,
          maxWidth: 800,
          maxHeight: '90vh',
          overflow: 'auto',
          bgcolor: 'background.paper',
          boxShadow: 24,
          p: 4,
        }}
      >
        <form onSubmit={handleSubmit(onSubmit)}>
          <Box display="flex" flexDirection="column" gap={3}>
            <Typography variant="h6" align="center" gutterBottom>
              {t('whatsapp.templateMessenger.title')}
            </Typography>

            <TextField
              label={t('common.search')}
              placeholder={t('common.search')}
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              fullWidth
              size="small"
            />

            <Controller
              name="templateId"
              control={control}
              render={({ field }) => (
                <Select {...field} displayEmpty fullWidth error={!!errors.templateId}>
                  <MenuItem value="" disabled>
                    {t('whatsapp.templateMessenger.selectTemplate')}
                  </MenuItem>
                  {templateOptions.map((tpl) => (
                    <MenuItem key={tpl.id} value={tpl.id}>
                      {tpl.elementName} {tpl.templateType ? `(${tpl.templateType.toLowerCase()})` : ''}
                    </MenuItem>
                  ))}
                </Select>
              )}
            />

            {selectedTemplate && (
              <>
                <Box>
                  <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                    {t('whatsapp.templateMessenger.preview')}
                  </Typography>
                  <Typography
                    variant="body1"
                    sx={{
                      p: 2,
                      bgcolor: palette.paper.dark,
                      borderRadius: 1,
                      whiteSpace: 'pre-wrap',
                    }}
                  >
                    {previewText}
                  </Typography>
                </Box>

                {requiresFile && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('whatsapp.templateMessenger.uploadFile')}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Button
                        variant="outlined"
                        onClick={() => fileInputRef.current?.click()}
                        startIcon={<AttachFile />}
                      >
                        {t('common.selectFile')}
                      </Button>
                      <input
                        ref={fileInputRef}
                        type="file"
                        onChange={handleFileChange}
                        accept={isImageTemplate ? 'image/*' : '*/*'}
                        style={{ display: 'none' }}
                      />
                      {selectedFile ? (
                        <Typography
                          variant="body2"
                          sx={{ ml: 1, flexGrow: 1, overflow: 'hidden', textOverflow: 'ellipsis' }}
                        >
                          {selectedFile.name}
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          {t('whatsapp.templateMessenger.noFileSelected')}
                        </Typography>
                      )}
                    </Box>
                    {fileError && <FormHelperText error>{fileError}</FormHelperText>}
                  </Box>
                )}

                {fields.length > 0 && (
                  <Box>
                    <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                      {t('whatsapp.templateMessenger.completeFields')}
                    </Typography>
                    <Box display="flex" flexDirection="column" gap={2}>
                      {fields.map((field, idx) => (
                        <ControlledTextField
                          key={field.id}
                          control={control}
                          name={`placeholders.${idx}.value`}
                          placeholder={t('whatsapp.templateMessenger.placeholder', { number: idx + 1 })}
                          required
                          errorMessage={errors.placeholders?.[idx]?.value?.message}
                          fullWidth
                        />
                      ))}
                    </Box>
                  </Box>
                )}
              </>
            )}

            <Box display="flex" justifyContent="space-between" mt={2}>
              <Button onClick={handleClose} variant="outlined">
                {t('common.cancel')}
              </Button>
              <LoadingButton
                variant="contained"
                color="primary"
                type="submit"
                disabled={!canSubmit()}
                loading={sendTemplate.isLoading || sendTemplateWithFile.isLoading}
              >
                {t('whatsapp.templateMessenger.sendMessage')}
              </LoadingButton>
            </Box>
          </Box>
        </form>
      </Box>
    </Modal>
  );
};
